import React, { useEffect, useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const ALLOWED_DEPT_IDS = [26, 75, 99, 109, 110, 192, 210, 211, 214];
const DAY_CODES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const JOB_TYPES = [
  { value: 'P', label: 'Permanent' },
  { value: 'T', label: 'Temporary Basis' },
  { value: 'C', label: 'Contractual' },
  { value: 'D', label: 'Daily Wages' },
  { value: 'M', label: 'MTS' },
];

function pad(n) { return String(n).padStart(2, '0'); }

function localDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function firstOfMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
}

function getDayCode(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return DAY_CODES[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}

function dayNum(dateStr) {
  return parseInt(dateStr.slice(8, 10), 10);
}

export default function Attendance() {
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);

  const moduleId   = parseInt(localUser?.moduleId, 10);
  const userDeptId = parseInt(localUser?.deptid, 10);

  const isDeptScoped = [2, 15].includes(moduleId);

  const [fromDate,    setFromDate]    = useState(firstOfMonthStr());
  const [toDate,      setToDate]      = useState(localDateStr());
  const [empType,     setEmpType]     = useState('N');
  const [jobType,     setJobType]     = useState('P');
  const [deptId,      setDeptId]      = useState(isDeptScoped ? String(userDeptId) : '');
  const [departments, setDepartments] = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [report,      setReport]      = useState(null);

  useEffect(() => {
    if (![2, 4, 12, 15].includes(moduleId)) return;
    fetch(`${API_ROOT}/api/directory/departments`)
      .then(r => r.json())
      .then(data => {
        let depts = Array.isArray(data) ? data : [];
        if (isDeptScoped) {
          depts = depts.filter(d => d.DepartmentID === userDeptId);
        } else {
          depts = depts.filter(d => ALLOWED_DEPT_IDS.includes(d.DepartmentID));
        }
        setDepartments(depts);
        if (depts.length && !isDeptScoped) setDeptId(String(depts[0].DepartmentID));
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (![2, 4, 12, 15].includes(moduleId)) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  const handleView = async (e) => {
    e.preventDefault();
    if (!deptId) { setError("Please select a department."); return; }
    setError('');
    setReport(null);
    setLoading(true);
    try {
      const res  = await fetch(`${API_ROOT}/api/attendance/monthly-report-time-leave`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ fromDate, toDate, empType, jobType, departmentID: deptId, moduleId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch report.");
      setReport(data);
    } catch (err) {
      setError(err.message || "Failed to fetch report.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-3">
      <h4 className="ctitle">Monthly Attendance Register (Time With Leave)</h4>

      {error && <div className="alert alert-danger mt-2">{error}</div>}

      <form className="row g-2 align-items-end mb-3" onSubmit={handleView}>
        <div className="col-sm-2">
          <label className="form-label">From Date</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            required
          />
        </div>
        <div className="col-sm-2">
          <label className="form-label">To Date</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            required
          />
        </div>
        <div className="col-sm-2">
          <label className="form-label">Emp Type</label>
          <select
            className="form-select form-select-sm"
            value={empType}
            onChange={e => setEmpType(e.target.value)}
          >
            <option value="N">Non Teaching</option>
          </select>
        </div>
        <div className="col-sm-2">
          <label className="form-label">Job Type</label>
          <select
            className="form-select form-select-sm"
            value={jobType}
            onChange={e => setJobType(e.target.value)}
          >
            {JOB_TYPES.map(j => (
              <option key={j.value} value={j.value}>{j.label}</option>
            ))}
          </select>
        </div>
        <div className="col-sm-2">
          <label className="form-label">Department</label>
          {isDeptScoped ? (
            <input
              type="text"
              className="form-control form-control-sm"
              value={departments[0]?.Department || ''}
              readOnly
            />
          ) : (
            <select
              className="form-select form-select-sm"
              value={deptId}
              onChange={e => setDeptId(e.target.value)}
            >
              <option value="">-- Select --</option>
              {departments.map(d => (
                <option key={d.DepartmentID} value={String(d.DepartmentID)}>{d.Department}</option>
              ))}
            </select>
          )}
        </div>
        <div className="col-sm-2">
          <button type="submit" className="btn btn-primary btn-sm me-2" disabled={loading}>
            {loading ? "Loading..." : "View"}
          </button>
          {report && report.employees.length > 0 && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>
              Print
            </button>
          )}
        </div>
      </form>

      {report && report.employees.length === 0 && (
        <div className="alert alert-info">No records found for the selected criteria.</div>
      )}

      {report && report.employees.length > 0 && (
        <div className="table-responsive" style={{ fontSize: '11px' }}>
          <table className="table table-bordered table-sm" style={{ whiteSpace: 'nowrap' }}>
            <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
              <tr>
                <th style={{ width: '35px', textAlign: 'center' }}>SNo</th>
                <th style={{ minWidth: '130px' }}>Name</th>
                {report.dates.map(d => (
                  <th key={d} style={{ width: '46px', textAlign: 'center', padding: '2px 1px' }}>
                    {dayNum(d)}
                  </th>
                ))}
                <th style={{ width: '32px', textAlign: 'center' }}>P</th>
                <th style={{ width: '32px', textAlign: 'center' }}>L</th>
                <th style={{ width: '32px', textAlign: 'center' }}>A</th>
              </tr>
              <tr style={{ fontSize: '10px', opacity: 0.85 }}>
                <th></th>
                <th>Designation / Label</th>
                {report.dates.map(d => (
                  <th key={d} style={{ textAlign: 'center', padding: '1px' }}>{getDayCode(d)}</th>
                ))}
                <th></th><th></th><th></th>
              </tr>
            </thead>
            <tbody>
              {report.employees.map(emp => (
                <React.Fragment key={emp.sno}>
                  {/* Row 1: TimeIn */}
                  <tr>
                    <td rowSpan={3} style={{ textAlign: 'center', verticalAlign: 'middle', fontWeight: 'bold' }}>
                      {emp.sno}
                    </td>
                    <td style={{ fontWeight: 'bold', minWidth: '130px' }}>
                      {emp.empName}
                    </td>
                    {emp.days.map(d => (
                      <td key={d.date} style={{ textAlign: 'center', color: d.timeInColor, padding: '1px 2px' }}>
                        {d.timeInStatus}
                      </td>
                    ))}
                    <td></td><td></td><td></td>
                  </tr>
                  {/* Row 2: TimeOut */}
                  <tr>
                    <td style={{ color: '#555' }}>{emp.designation}</td>
                    {emp.days.map(d => (
                      <td key={d.date} style={{ textAlign: 'center', color: d.timeOutColor, padding: '1px 2px' }}>
                        {d.timeOutStatus}
                      </td>
                    ))}
                    <td></td><td></td><td></td>
                  </tr>
                  {/* Row 3: Final Status */}
                  <tr style={{ borderBottom: '2px solid #888' }}>
                    <td style={{ color: '#777' }}>Leave Status</td>
                    {emp.days.map(d => (
                      <td key={d.date} style={{ textAlign: 'center', color: d.finalColor, fontWeight: 'bold', padding: '1px 2px' }}>
                        {d.finalStatus}
                      </td>
                    ))}
                    <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{emp.counterP}</td>
                    <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{emp.counterL}</td>
                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: 'red' }}>{emp.counterA}</td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
